import { useState, useEffect } from 'react';
import states from '../../../assets/data-dictionary/states.json';
import cities from '../../../assets/data-dictionary/cities.json';
import b2bCities from '../../../assets/data-dictionary/b2b_cities.json';
import MultiSelectInput from '../../inputs/MultiSelectInput';
import debounce from 'debounce';

// PR 2b-6: Both formatters are now IDEMPOTENT.
//
// When the audience tree restructures (e.g., adding a second variable
// promotes a leaf root to AND[leaf, newLeaf]), React unmounts and remounts
// the existing card because its parent component type changes. The
// remounted MultiSelectGeography re-runs its useEffect with the saved
// `initialState`, which is already in UI shape (`[{label, value}]`).
// Previously the formatters blindly wrapped each entry, producing the
// double-wrapped `{label: {label, value}, value: {label, value}}` that
// react-select tries (and fails) to render as a React child.
//
// Now: detect the shape per entry and pass through if already wrapped.

function isAlreadyFormatted(entry: unknown): entry is { label: string; value: string } {
  return (
    typeof entry === 'object' &&
    entry !== null &&
    'label' in entry &&
    'value' in entry
  );
}

function formatValueFromState(state) {
  if (!state?.values) return null;
  return state.values
    .map((entry) => {
      if (isAlreadyFormatted(entry)) return entry;
      // API shape: a state code string (e.g., "KS"). Look up the full name.
      const stateCode = String(entry);
      const matchingState = states.find((s) => s.code === stateCode);
      if (!matchingState) return null;
      return { label: matchingState.name, value: matchingState.code };
    })
    .filter(Boolean);
}

function formatValueFromCity(city) {
  if (!city?.values) return null;
  return city.values.map((entry) => {
    if (isAlreadyFormatted(entry)) return entry;
    // API shape: a city name string. Wrap it.
    const cityName = String(entry);
    return { label: cityName, value: cityName };
  });
}

type MultiSelectGeographyProps = {
  name: string;
  attrs?: {
    key?: string;
  };
  setVariablesState: Function;
  initialState: any;
};

export default function MultiSelectGeography({
  name,
  attrs,
  setVariablesState,
  initialState,
}: MultiSelectGeographyProps) {
  const [value, setValue] = useState();

  let options;
  const isStateField = name.toLowerCase() === 'state' || attrs?.key === 'B2B_State';
  const isCityField = name.toLowerCase() === 'city' || attrs?.key === 'B2B_City';

  useEffect(() => {
    if (initialState) {
      if (isStateField) {
        const formattedState = formatValueFromState(initialState);
        setValue(formattedState);
        if (typeof setVariablesState === 'function') {
          setVariablesState(
            { values: formattedState },
            { loadingInitialState: true }
          );
        }
      }

      if (isCityField) {
        const formattedState = formatValueFromCity(initialState);
        setValue(formattedState);
        if (typeof setVariablesState === 'function') {
          setVariablesState(
            { values: formattedState },
            { loadingInitialState: true }
          );
        }
      }
    }
  }, []);

  function handleChange(optionsSelected, actionState) {
    setValue(optionsSelected);
    if (typeof setVariablesState === 'function') {
      setVariablesState({ values: optionsSelected });
    }
  }

  function handleBlur() {
    if (typeof setVariablesState === 'function') {
      setVariablesState({ values: value });
    }
  }

  function handleLoadOptions(query: string): Promise<any[]> {
    return new Promise((resolve) => {
      if (!query || query.trim() === '') {
        resolve([]);
        return;
      }

      const debounced = debounce(() => {
        // Use b2b_cities for B2B_City, otherwise use regular cities
        const cityList = attrs?.key === 'B2B_City' ? b2bCities : cities;
        const filteredCities = cityList
          .filter((cityName) =>
            cityName.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 50) // Limit to 50 results for performance
          .map((cityName) => ({
            label: cityName,
            value: cityName,
          }));

        resolve(filteredCities);
      }, 300);

      debounced();
    });
  }

  let Component = <></>;

  if (isStateField) {
    options = states.map(({ name, code }) => ({
      label: name,
      value: code,
    }));
    Component = (
      <MultiSelectInput
        options={options}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    );
  }

  if (isCityField) {
    Component = (
      <MultiSelectInput
        async={true}
        loadOptions={handleLoadOptions}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        noOptionsMessage="No matching cities."
      />
    );
  }

  return (
    <div className="flex flex-col w-full border-l-2 border-gray-400 px-2 mb-2">
      <div className="mb-1">{name}</div>
      {Component}
    </div>
  );
}
